import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// Types
export type PermissionRole = 'none' | 'read' | 'write' | 'admin';

export interface EnvironmentPermission {
  id: string;
  environment_id: string;
  user_id: string;
  role: PermissionRole;
  user_email: string;
  user_name: string;
  granted_by: string;
  created_at: string;
  updated_at: string;
}

export interface MyEnvironmentPermission {
  environment_id: string;
  environment_name: string;
  role: PermissionRole;
  can_read: boolean;
  can_write: boolean;
  can_admin: boolean;
}

export interface MyPermissionsResponse {
  permissions: MyEnvironmentPermission[];
  is_team_admin: boolean;
}

export interface EnvironmentPermissionsResponse {
  permissions: EnvironmentPermission[];
}

export interface DefaultPermission {
  id: string;
  project_id: string;
  environment_name: string;
  default_role: PermissionRole;
  created_at: string;
}

export interface DefaultsResponse {
  defaults: DefaultPermission[];
}

export interface SetPermissionRequest {
  role: PermissionRole;
}

export interface BulkSetPermissionsRequest {
  permissions: Array<{
    user_id: string;
    role: PermissionRole;
  }>;
}

export interface SetDefaultsRequest {
  defaults: Array<{
    environment_name: string;
    default_role: PermissionRole;
  }>;
}

// Role display info
export const PERMISSION_ROLES: { value: PermissionRole; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'No Access',
    description: 'Cannot view or modify secrets in this environment',
  },
  {
    value: 'read',
    label: 'Read',
    description: 'Can view secrets but not modify them',
  },
  {
    value: 'write',
    label: 'Write',
    description: 'Can view and modify secrets',
  },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full access including managing permissions',
  },
];

/**
 * Get current user's permissions for all environments in a project
 */
export function useMyPermissions(projectId: string | null) {
  return useQuery({
    queryKey: ['my-permissions', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await apiClient.get<MyPermissionsResponse>(
        `/api/v1/projects/${projectId}/my-permissions`
      );
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * List all permissions for a specific environment
 */
export function useEnvironmentPermissions(projectId: string | null, environmentName: string | null) {
  return useQuery({
    queryKey: ['environment-permissions', projectId, environmentName],
    queryFn: async () => {
      if (!projectId || !environmentName) return { permissions: [] };
      const response = await apiClient.get<EnvironmentPermissionsResponse>(
        `/api/v1/projects/${projectId}/environments/${environmentName}/permissions`
      );
      return response.data;
    },
    enabled: !!projectId && !!environmentName,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Mutation to set a user's permission for an environment
 */
export function useSetPermission(projectId: string | null, environmentName: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: PermissionRole }) => {
      if (!projectId || !environmentName) throw new Error('Project ID and environment name are required');
      const response = await apiClient.put<EnvironmentPermission>(
        `/api/v1/projects/${projectId}/environments/${environmentName}/permissions/${userId}`,
        { role }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-permissions', projectId, environmentName] });
      queryClient.invalidateQueries({ queryKey: ['my-permissions', projectId] });
    },
  });
}

/**
 * Mutation to delete a user's permission for an environment
 */
export function useDeletePermission(projectId: string | null, environmentName: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (!projectId || !environmentName) throw new Error('Project ID and environment name are required');
      await apiClient.delete(
        `/api/v1/projects/${projectId}/environments/${environmentName}/permissions/${userId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-permissions', projectId, environmentName] });
      queryClient.invalidateQueries({ queryKey: ['my-permissions', projectId] });
    },
  });
}

/**
 * Mutation to bulk set permissions for an environment
 */
export function useBulkSetPermissions(projectId: string | null, environmentName: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (permissions: BulkSetPermissionsRequest['permissions']) => {
      if (!projectId || !environmentName) throw new Error('Project ID and environment name are required');
      const response = await apiClient.put(
        `/api/v1/projects/${projectId}/environments/${environmentName}/permissions`,
        { permissions }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-permissions', projectId, environmentName] });
      queryClient.invalidateQueries({ queryKey: ['my-permissions', projectId] });
    },
  });
}

/**
 * Get default permissions for a project
 */
export function useProjectDefaults(projectId: string | null) {
  return useQuery({
    queryKey: ['project-defaults', projectId],
    queryFn: async () => {
      if (!projectId) return { defaults: [] };
      const response = await apiClient.get<DefaultsResponse>(
        `/api/v1/projects/${projectId}/permissions/defaults`
      );
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Mutation to set default permissions for a project
 */
export function useSetProjectDefaults(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (defaults: SetDefaultsRequest['defaults']) => {
      if (!projectId) throw new Error('Project ID is required');
      const response = await apiClient.put(
        `/api/v1/projects/${projectId}/permissions/defaults`,
        { defaults }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-defaults', projectId] });
    },
  });
}

/**
 * Helper to check if a user can manage permissions for an environment
 */
export function canManagePermissions(
  myPermissions: MyPermissionsResponse | null | undefined,
  environmentName: string
): boolean {
  if (!myPermissions) return false;
  if (myPermissions.is_team_admin) return true;

  const envPermission = myPermissions.permissions.find(
    (p) => p.environment_name === environmentName
  );
  return envPermission?.can_admin ?? false;
}

/**
 * Helper to get role badge variant
 */
export function getRoleBadgeVariant(role: PermissionRole): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (role) {
    case 'admin':
      return 'default';
    case 'write':
      return 'success';
    case 'read':
      return 'secondary';
    case 'none':
      return 'outline';
    default:
      return 'outline';
  }
}
